import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Tag, PhotoFrame, Reveal, SectionHead, EmptyState } from "@/components/Shared";
import { api, fmtDate, IMG } from "@/lib/api";

function Block({ title, children }) {
  return (
    <Reveal>
      <div className="mb-12" data-testid="project-detail-block">
        <p className="eyebrow flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-ember" /> {title}
        </p>
        <div className="mt-4 text-sm leading-relaxed text-silver-dim sm:text-base">{children}</div>
      </div>
    </Reveal>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/projects/${id}`)
      .then(({ data }) => setP(data))
      .catch(() => setP(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-silver-muted">Loading project…</p>
      </main>
    );
  }

  if (!p) {
    return (
      <main className="mx-auto max-w-3xl px-6 pb-32 pt-48">
        <EmptyState title="Project not found" note="It may have been removed. Browse all projects instead." />
        <div className="mt-8 text-center">
          <Link to="/projects" className="btn-silver" data-testid="project-notfound-back-btn">
            <span>Back to Projects</span>
          </Link>
        </div>
      </main>
    );
  }

  const stats = [
    { label: "Participants", value: p.participants || "—" },
    { label: "Beneficiaries", value: p.beneficiaries || "—" },
    { label: "Collaborators", value: p.collaborators || "—" },
  ];

  return (
    <main>
      <section className="relative overflow-hidden pb-16 pt-40" data-testid="project-hero">
        <div className="absolute inset-0">
          <img src={IMG.forestMist} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/50 to-ink" />
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-6">
          <Link to="/projects" className="nav-link inline-flex items-center gap-2" data-testid="project-back-link">
            <ArrowLeft size={14} /> All Projects
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Tag tone="ember">{p.category}</Tag>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-silver-muted">{fmtDate(p.date)}</span>
          </div>
          <h1 className="mt-6 font-display text-4xl font-semibold leading-tight sm:text-6xl">
            <span className="text-silver">{p.title}</span>
          </h1>
          {p.description && <p className="mt-6 max-w-2xl text-sm leading-relaxed text-silver-dim sm:text-base">{p.description}</p>}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-12" data-testid="project-body">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            {p.about && <Block title="About the Project">{p.about}</Block>}
            {p.objective && <Block title="Objective">{p.objective}</Block>}
            {p.what_we_did && <Block title="What We Did">{p.what_we_did}</Block>}
            {p.impact && <Block title="Impact">{p.impact}</Block>}
            {p.related && <Block title="Related Articles & Publications">{p.related}</Block>}
          </div>
          <div className="self-start">
            <PhotoFrame
              src={p.cover_image}
              alt={p.title}
              label="Project photograph — add via Admin"
              className="aspect-[4/5] w-full bg-navy"
            />
            <div className="mt-8 grid grid-cols-3 gap-3" data-testid="project-stats">
              {stats.map((s) => (
                <div key={s.label} className="card-line p-4 text-center">
                  <p className="truncate font-display text-lg text-silver" title={s.value}>{s.value}</p>
                  <p className="mt-1 font-mono text-[8px] uppercase tracking-[0.18em] text-silver-muted">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {(p.gallery || []).length > 0 && (
          <div className="mt-16">
            <SectionHead eyebrow="Moments" title="From the" italic="project" />
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {p.gallery.map((g, i) => (
                <PhotoFrame key={i} src={g} alt={`${p.title} gallery ${i + 1}`} label="Project photograph" className="aspect-video" />
              ))}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
